// The विभक्ति × वचन table of one noun or pronoun: case rows, एकवचन and बहुवचन columns (Prakrit has no dual). Every
// cell is a button; the chosen one is a saffron pill. Arrow keys move between cells and choose as they go; Home / End
// jump to the ends. It sits inside the "१. सामान्य · २. विकल्प" tabs (KalTabs), for Kids and Adults alike: सामान्य shows
// the core forms only; विकल्प shows the core forms and then the variant forms in terracotta with a "विकल्प" marker,
// or a muted "—" when the cell has none.

import type { FC, KeyboardEvent, ReactNode } from 'react'
import type { Case, Num } from '@/prakrit/lib/grammar/types'
import type { GridCell } from '@/prakrit/components/grammar/grammar-grid'
import type { KalMode } from '@/prakrit/components/grammar/kal/kal-tabs'

import { cn } from '@/lib/utils'
import { CASES, NUMBERS } from '@/prakrit/lib/grammar/types'
import { CASE_LABEL, NUMBER_LABEL } from '@/prakrit/lib/grammar/labels'
import { StatusBadge, reviewBadgeKind } from '@/prakrit/components/common/status-badge'
import { titleCase } from '@/prakrit/components/grammar/shabd/case-info'

export const NUMBER_HEAD: Record<Num, string> = { eka: 'Singular (one)', bahu: 'Plural (two or more)' }

export type CaseCells = Partial<Record<Case, Record<Num, GridCell>>>

/** How a cell was chosen: a tap or click (also Enter / Space), or an arrow key moving through the table. */
export type SelectSource = 'click' | 'arrow'

/** True when any cell of the table has a विकल्प form. */
export const cellsHaveAlt = (cells: CaseCells): boolean => CASES.some(c => NUMBERS.some(n => (cells[c]?.[n]?.alt.length ?? 0) > 0))

const CAPTION: Record<KalMode, { hi: string, en: string }> = {
    samanya: { hi: 'सामान्य रूप', en: 'standard forms' },
    vikalp: { hi: 'सामान्य और विकल्प रूप', en: 'standard and variant forms' }
}

interface DeclensionTableProps {
    cells: CaseCells
    cases: Case[]
    mode: KalMode
    /** The word in the caption: the noun stem (पुरिस) or the pronoun base (अम्ह). */
    word: string
    /** Its meaning, read out with the caption. */
    gloss?: string
    selected: { case: Case, number: Num }
    onSelect: (c: Case, n: Num, source: SelectSource) => void
    /** Cells tinted beside the chosen one, e.g. its चतुर्थी = षष्ठी twin. */
    highlight?: { case: Case, number: Num }[]
    /** A marker in each cell whose core form is not yet verified (StatusBadge shows it in review view only). */
    showReview?: boolean
    className?: string
}

export const DeclensionTable: FC<DeclensionTableProps> = ({
    cells,
    cases,
    mode,
    word,
    gloss,
    selected,
    onSelect,
    highlight = [],
    showReview = false,
    className
}): ReactNode => {
    const visible = cases.includes(selected.case)
    const vikalp = mode === 'vikalp'

    const onKeyDown = (event: KeyboardEvent<HTMLTableSectionElement>) => {
        const target = event.target as HTMLElement
        const row = Number(target.dataset.row)
        const col = Number(target.dataset.col)

        if (Number.isNaN(row) || Number.isNaN(col)) return

        let r = row
        let c = col

        if (event.key === 'ArrowDown') r = Math.min(cases.length - 1, row + 1)
        else if (event.key === 'ArrowUp') r = Math.max(0, row - 1)
        else if (event.key === 'ArrowRight') c = Math.min(NUMBERS.length - 1, col + 1)
        else if (event.key === 'ArrowLeft') c = Math.max(0, col - 1)
        else if (event.key === 'Home') {
            r = event.ctrlKey ? 0 : row
            c = 0
        }
        else if (event.key === 'End') {
            r = event.ctrlKey ? cases.length - 1 : row
            c = NUMBERS.length - 1
        }
        else return

        event.preventDefault()

        if (r === row && c === col) return

        event.currentTarget.querySelector<HTMLElement>(`[data-row="${r}"][data-col="${c}"]`)?.focus()
        onSelect(cases[r], NUMBERS[c], 'arrow')
    }

    return (
        <div className={cn('overflow-hidden rounded-3xl border border-border bg-card shadow-card', className)}>
            <table className='w-full table-fixed border-collapse text-left'>
                <caption className='border-b border-border px-4 py-3 text-left sm:px-5'>
                    <span lang='pra' className='font-prakrit text-base font-semibold text-prakrit'>{word}</span>
                    <span aria-hidden='true' className='text-muted-foreground'> — </span>
                    <span lang='hi' className='hindi text-base font-semibold'>{CAPTION[mode].hi}</span>
                    <span aria-hidden='true' className='text-muted-foreground'> · </span>
                    <span lang='en' className='text-sm text-muted-foreground'>{CAPTION[mode].en}</span>
                    <span className='sr-only'>
                        {gloss ? ` (${gloss})` : ''}. विभक्ति × वचन · cases × numbers. तीर-कुंजियों से खानों में घूमें · Move between cells with the arrow keys.
                    </span>
                </caption>
                <colgroup>
                    <col className='w-[31%] sm:w-[28%]' />
                    <col />
                    <col />
                </colgroup>
                <thead className='bg-surface-muted/80'>
                    <tr className='border-b border-border'>
                        <th scope='col' className='px-3 py-3 align-bottom font-normal sm:px-5'>
                            <span lang='hi' className='hindi block text-sm font-semibold'>विभक्ति</span>
                            <span lang='en' className='block text-xs text-muted-foreground'>Case</span>
                        </th>
                        {NUMBERS.map(n => (
                            <th key={n} scope='col' className='px-3 py-3 align-bottom font-normal sm:px-5'>
                                <span lang='hi' className='hindi block text-sm font-semibold'>{NUMBER_LABEL[n].hi}</span>
                                <span lang='en' className='block text-xs text-muted-foreground'>{NUMBER_HEAD[n]}</span>
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody onKeyDown={onKeyDown} className='divide-y divide-border'>
                    {cases.map((c, row) => (
                        <tr key={c}>
                            <th scope='row' className='px-3 py-2 align-middle font-normal sm:px-5'>
                                <span lang='hi' className='hindi block text-[0.9375rem] font-bold leading-tight'>{CASE_LABEL[c].hi}</span>
                                <span lang='en' className='block text-xs text-muted-foreground'>{titleCase(CASE_LABEL[c].en)}</span>
                            </th>
                            {NUMBERS.map((n, col) => {
                                const cell = cells[c]?.[n]
                                const core = cell?.core ?? []
                                const alt = vikalp ? cell?.alt ?? [] : []
                                const on = selected.case === c && selected.number === n
                                const lit = !on && highlight.some(h => h.case === c && h.number === n)
                                const tabbable = on || (!visible && row === 0 && col === 0)
                                const review = showReview && cell?.review && cell.review !== 'verified' && cell.review !== 'published' ? cell.review : null
                                const variants = !vikalp ? '' : alt.length > 0 ? `; विकल्प · variant: ${alt.join(', ')}` : '; विकल्प नहीं · no variant'
                                const label = `${CASE_LABEL[c].hi} ${NUMBER_LABEL[n].hi} (${CASE_LABEL[c].en}, ${NUMBER_LABEL[n].en}): ${core.length > 0 ? core.join(', ') : '—'}${variants}`

                                return (
                                    <td key={n} className='px-1 py-1.5 sm:px-2'>
                                        <button
                                            type='button'
                                            data-row={row}
                                            data-col={col}
                                            tabIndex={tabbable ? 0 : -1}
                                            aria-pressed={on}
                                            aria-label={label}
                                            onClick={() => onSelect(c, n, 'click')}
                                            className={cn(
                                                'relative flex min-h-12 w-full min-w-0 flex-col items-start justify-center gap-0.5 rounded-2xl border-2 px-2.5 py-1 text-left transition-colors sm:px-3.5',
                                                on && 'border-saffron-strong/45 bg-saffron shadow-sm',
                                                lit && 'border-dashed border-saffron-strong/50 bg-saffron/10 hover:bg-saffron/15',
                                                !on && !lit && 'border-transparent hover:bg-saffron/10'
                                            )}
                                        >
                                            {core.length > 0 ? (
                                                <span lang='pra' className='pra break-words text-[1.0625rem] sm:text-[1.3125rem]'>{core.join(' / ')}</span>
                                            ) : (
                                                <span className='text-base text-muted-foreground'>—</span>
                                            )}
                                            {vikalp && (alt.length > 0 ? (
                                                <span className='flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5'>
                                                    <span lang='pra' className={cn('break-words font-prakrit text-sm font-semibold leading-snug sm:text-base', on ? 'text-foreground/80' : 'text-terracotta')}>
                                                        {alt.join(', ')}
                                                    </span>
                                                    <StatusBadge kind='alt' withEnglish={false} className={on ? 'border-foreground/35 text-foreground' : undefined} />
                                                </span>
                                            ) : (
                                                <span className={cn('text-sm leading-snug', on ? 'text-foreground/60' : 'text-muted-foreground')}>—</span>
                                            ))}
                                            {review && <StatusBadge kind={reviewBadgeKind(review)} withEnglish={false} className='absolute right-1.5 top-1.5' />}
                                        </button>
                                    </td>
                                )
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    )
}

interface VikalpNoteProps {
    /** The table has at least one विकल्प form. */
    hasAlt: boolean
    /** Whose table it is, for the note when there are no variants. */
    of?: 'word' | 'pronoun'
    className?: string
}

/** The line under the table on the विकल्प tab. */
export const VikalpNote: FC<VikalpNoteProps> = ({ hasAlt, of = 'word', className }): ReactNode => {
    const text = hasAlt
        ? { hi: 'विकल्प रूप भी सही हैं, पर अभ्यास में सामान्य रूप ही पूछा जाता है।', en: 'Variant forms are right too, but practice asks for the standard form.' }
        : of === 'pronoun'
            ? { hi: 'इस सर्वनाम के लिए पुस्तक में कोई विकल्प रूप नहीं।', en: 'The book gives no variant forms for this pronoun.' }
            : { hi: 'इस शब्द के लिए पुस्तक में कोई विकल्प रूप नहीं।', en: 'The book gives no variant forms for this word.' }

    return (
        <p className={cn('px-1 text-sm text-muted-foreground', className)}>
            <span lang='hi' data-ex='hi' className='hindi'>{text.hi}</span>
            <span aria-hidden='true' className='ex-sep'> · </span>
            <span lang='en' data-ex='en'>{text.en}</span>
        </p>
    )
}
